import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddCardIcon from '@mui/icons-material/AddCard';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { API_URL } from '../../../common/Constants';
import Cookies from '../../../common/Cookies';
import { formatUsdFromCents } from '../../../utils/formatCurrency';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
});

const formatPrice = (cents) => formatUsdFromCents(cents);

/** For display: annual shows total yearly price (backend sends yearly cents), monthly shows per-month */
const getRecurringDisplay = (recurringAmountCents, billingCycle) => {
  if (billingCycle === 'annual') {
    return { amount: recurringAmountCents, period: '/year' };
  }
  return { amount: recurringAmountCents, period: '/month' };
};

const formatCardBrand = (brand) => {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' }
    },
    invalid: {
      color: '#9e2146'
    }
  }
};

function AddCardForm({ onSuccess, onCancel, submitLabel = 'Save card' }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/payment/create-setup-intent`,
        {},
        { headers: getAuthHeaders() }
      );
      if (!res.data.success || !res.data.clientSecret) {
        setError(res.data.message || 'Failed to create setup');
        setSaving(false);
        return;
      }
      const cardElement = elements.getElement(CardElement);
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(res.data.clientSecret, {
        payment_method: { card: cardElement }
      });
      if (confirmError) {
        setError(confirmError.message || 'Card validation failed');
        setSaving(false);
        return;
      }
      const paymentMethodId = setupIntent?.payment_method ?? null;
      onSuccess(paymentMethodId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Card details
        </Typography>
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover'
          }}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button type="submit" variant="contained" size="small" disabled={saving}>
            {saving ? 'Processing...' : submitLabel}
          </Button>
          <Button type="button" size="small" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
}

/**
 * Modal for plan changes:
 * - New subscribers: redirects to Stripe Checkout
 * - Existing subscribers: shows amount due (or $0 for upgrade-back/downgrade), then change-plan
 * - In-app card entry for adding new payment methods
 */
const StripePaymentModal = ({ open, onClose, plan, planName, billingCycle, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('preview');
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  const fetchPreview = () => {
    if (!open || !plan || plan === 'B') return;
    setLoading(true);
    axios
      .post(
        `${API_URL}/payment/subscription-preview`,
        { plan, billing_cycle: billingCycle || 'monthly' },
        { headers: getAuthHeaders() }
      )
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setPreview(res.data);
          if (res.data.stripePublishableKey) {
            setStripePromise(loadStripe(res.data.stripePublishableKey));
          }
          if (res.data.hasExistingSubscription && (res.data.amountDue === 0 || res.data.isUpgradeBack) && !res.data.isDowngrade) {
            setStep('processing');
            axios
              .post(
                `${API_URL}/payment/change-plan`,
                { plan, billing_cycle: billingCycle || 'monthly' },
                { headers: getAuthHeaders() }
              )
              .then((changeRes) => {
                if (changeRes.data.success) {
                  onSuccess?.();
                  onClose();
                } else {
                  setError(changeRes.data.message || 'Failed to update plan');
                  setStep('preview');
                }
              })
              .catch((err) => {
                setError(err.response?.data?.message || err.message || 'Error updating plan');
                setStep('preview');
              });
          }
        } else {
          setError(res.data.message || 'Failed to load preview');
        }
      })
      .catch((err) => {
        setLoading(false);
        setError(err.response?.data?.message || err.message || 'Error loading preview');
      });
  };

  useEffect(() => {
    if (open && plan && plan !== 'B') {
      setError(null);
      setPreview(null);
      setStep('preview');
      setShowAddCardForm(false);
      fetchPreview();
    }
  }, [open, plan, billingCycle]);

  useEffect(() => {
    if (open) {
      axios.get(`${API_URL}/payment/config`, { headers: getAuthHeaders() })
        .then((res) => {
          const pk = res.data?.stripePublishableKey;
          if (pk) setStripePromise(loadStripe(pk));
        })
        .catch(() => {});
    }
  }, [open]);

  const handleConfirmPayment = () => {
    if (!preview?.hasExistingSubscription) return;
    if (preview.amountDue === 0 && !preview.isDowngrade) return;
    setStep('processing');
    setError(null);
    axios
      .post(
        `${API_URL}/payment/change-plan`,
        { plan, billing_cycle: billingCycle || 'monthly' },
        { headers: getAuthHeaders() }
      )
      .then((res) => {
        if (res.data.success) {
          onSuccess?.();
          onClose();
        } else {
          setError(res.data.message || 'Failed to update plan');
          setStep('preview');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Error updating plan');
        setStep('preview');
      });
  };

  const handleAddNewCardPortal = () => {
    setPortalLoading(true);
    setError(null);
    axios
      .post(
        `${API_URL}/payment/create-portal-session`,
        { return_url: window.location.href },
        { headers: getAuthHeaders() }
      )
      .then((res) => {
        if (res.data.success && res.data.url) {
          window.location.href = res.data.url;
        } else {
          setError(res.data.message || 'Failed to open payment settings');
          setPortalLoading(false);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Error opening payment settings');
        setPortalLoading(false);
      });
  };

  const handleSubscribe = (paymentMethodId) => {
    if (!paymentMethodId) return;
    setStep('processing');
    setError(null);
    axios
      .post(
        `${API_URL}/payment/create-subscription`,
        {
          plan,
          billing_cycle: billingCycle || 'monthly',
          payment_method_id: paymentMethodId
        },
        { headers: getAuthHeaders() }
      )
      .then((res) => {
        if (res.data.success) {
          onSuccess?.();
          onClose();
        } else {
          setError(res.data.message || 'Failed to create subscription');
          setStep('preview');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Error creating subscription');
        setStep('preview');
      });
  };

  const handleCardAdded = (paymentMethodId) => {
    setShowAddCardForm(false);
    const hasExisting = preview?.hasExistingSubscription ?? false;
    if (!hasExisting && paymentMethodId) {
      handleSubscribe(paymentMethodId);
    } else {
      fetchPreview();
    }
  };

  const handlePayAtStripe = () => {
    setStep('processing');
    setError(null);
    const isExistingWithCharge = hasExisting && amountDue > 0;
    const url = isExistingWithCharge
      ? `${API_URL}/payment/create-plan-change-checkout`
      : `${API_URL}/payment/create-checkout-session`;
    const body = isExistingWithCharge
      ? { plan, billing_cycle: billingCycle || 'monthly', amount_due: amountDue, plan_name: planName }
      : { plan, billing_cycle: billingCycle || 'monthly' };
    axios
      .post(url, body, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data.success && res.data.url) {
          window.location.href = res.data.url;
        } else {
          setError(res.data.message || 'Failed to open Stripe');
          setStep('preview');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Error connecting to payment');
        setStep('preview');
      });
  };

  const recurringAmount = preview?.recurringAmount ?? 0;
  const amountDue = preview?.amountDue ?? 0;
  const hasExisting = preview?.hasExistingSubscription ?? false;
  const isDowngrade = preview?.isDowngrade ?? false;
  const periodEndTimestamp = preview?.periodEndTimestamp;
  const daysRemainingInMonth = preview?.daysRemainingInMonth;
  const nextBillingDateTimestamp = preview?.nextBillingDateTimestamp;
  const nextPaymentDueTimestamp = preview?.nextPaymentDueTimestamp ?? nextBillingDateTimestamp;
  const currentBillingCycle = preview?.currentBillingCycle;
  const prorationLineItems = preview?.prorationLineItems ?? [];
  const paymentMethods = preview?.paymentMethods ?? [];
  const isBillingCycleChange = hasExisting && currentBillingCycle && (billingCycle || 'monthly') !== currentBillingCycle;
  const isAnnualToMonthly =
    isBillingCycleChange &&
    String(currentBillingCycle).toLowerCase() === 'annual' &&
    String(billingCycle || 'monthly').toLowerCase() === 'monthly';
  const defaultCard = paymentMethods.find((m) => m.isDefault) || paymentMethods[0];
  const displayBillingCycle = preview?.billingCycle ?? billingCycle ?? 'monthly';
  const { amount: displayAmount, period: displayPeriod } = getRecurringDisplay(recurringAmount, displayBillingCycle);
  const isNewSubMonthly = !hasExisting && (billingCycle || 'monthly') === 'monthly';

  const formatPeriodEnd = () => {
    if (!periodEndTimestamp) return 'end of your billing period';
    const d = new Date(periodEndTimestamp * 1000);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatNextPaymentDue = () => {
    if (nextPaymentDueTimestamp == null) return null;
    const d = new Date(nextPaymentDueTimestamp * 1000);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Payment</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Loading...</Typography>
          </Box>
        )}
        {!loading && preview && step === 'preview' && (
          <Box sx={{ py: 2 }}>
            {isDowngrade && amountDue <= 0 ? (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You will be switched to <strong>{planName}</strong> after {formatPeriodEnd()}.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Your new rate will be <strong>{formatPrice(displayAmount)}{displayPeriod}</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: 'success.main' }}>
                  No need to pay anything at this moment.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Switching to <strong>{planName}</strong>
                  {isBillingCycleChange && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {currentBillingCycle === 'monthly' ? 'Monthly → Annual billing' : 'Annual → Monthly billing'}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Recurring rate: <strong>{formatPrice(displayAmount)}{displayPeriod}</strong>
                </Typography>
                {hasExisting && amountDue > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {Array.isArray(prorationLineItems) && prorationLineItems.length > 0 ? (
                      <>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Proration breakdown
                        </Typography>
                        {prorationLineItems.map((item, idx) => {
                          const amt = item.amount != null ? item.amount : 0;
                          const formatted = formatPrice(amt);
                          return (
                            <Typography key={idx} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                              <span>{item.description || 'Proration'}</span>
                              <span>{formatted}</span>
                            </Typography>
                          );
                        })}
                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                          Amount due today: <strong>{formatPrice(amountDue)}</strong>
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1">
                        Amount due today: <strong>{formatPrice(amountDue)}</strong>
                      </Typography>
                    )}
                  </Box>
                )}
                {hasExisting && amountDue === 0 && !isDowngrade && (
                  <Typography variant="body1" sx={{ mb: 2, color: 'success.main' }}>
                    No charge today. Your plan will be updated.
                  </Typography>
                )}
                {!hasExisting && (
                  <>
                    {isNewSubMonthly && amountDue > 0 ? (
                      <>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Amount due today: <strong>{formatPrice(amountDue)}</strong>
                          {daysRemainingInMonth != null ? (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}(prorated for {daysRemainingInMonth} day{daysRemainingInMonth !== 1 ? 's' : ''} in this billing
                              period)
                            </Typography>
                          ) : (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}(prorated for the remainder of this billing period)
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          Then <strong>{formatPrice(recurringAmount)}/month</strong>, renewing at the end of each month.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        You will be charged <strong>{formatPrice(displayAmount)}{displayPeriod}</strong> today.
                      </Typography>
                    )}
                  </>
                )}
                {formatNextPaymentDue() && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Next payment due:{' '}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: isAnnualToMonthly ? 'error.main' : 'text.primary'
                      }}
                    >
                      {formatNextPaymentDue()}
                    </Box>
                  </Typography>
                )}
              </>
            )}
            {(!hasExisting || (hasExisting && !(isDowngrade && amountDue <= 0)) || paymentMethods.length > 0) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Payment method
                </Typography>
                {showAddCardForm && stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <AddCardForm
                      onSuccess={handleCardAdded}
                      onCancel={() => setShowAddCardForm(false)}
                      submitLabel={hasExisting ? 'Save card' : 'Subscribe'}
                    />
                  </Elements>
                ) : paymentMethods.length > 0 ? (
                  <>
                    <Card variant="outlined" sx={{ bgcolor: 'action.hover', mb: 1 }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CreditCardIcon color="action" fontSize="small" />
                          <Typography variant="body2">
                            {formatCardBrand(defaultCard?.brand)} •••• {defaultCard?.last4}
                          </Typography>
                          {defaultCard?.isDefault && (
                            <Chip label="Default" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        {paymentMethods.length > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {paymentMethods.length} card(s) on file. Charges go to default.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        size="small"
                        startIcon={portalLoading ? <CircularProgress size={14} /> : <AddCardIcon />}
                        onClick={() => setShowAddCardForm(true)}
                        disabled={portalLoading || !stripePromise}
                        sx={{ textTransform: 'none' }}
                      >
                        Add new card
                      </Button>
                      <Button
                        size="small"
                        onClick={handleAddNewCardPortal}
                        disabled={portalLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        {portalLoading ? 'Opening…' : 'Change payment method'}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      No payment method on file.
                    </Typography>
                    {stripePromise ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddCardIcon />}
                        onClick={() => setShowAddCardForm(true)}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                      >
                        Add credit card
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={portalLoading ? <CircularProgress size={14} /> : <AddCardIcon />}
                        onClick={handleAddNewCardPortal}
                        disabled={portalLoading}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                      >
                        Add credit card (via Stripe)
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
        {step === 'processing' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>
              {hasExisting ? 'Updating your plan...' : 'Creating subscription...'}
            </Typography>
          </Box>
        )}
        {error && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || step === 'processing'}>
          Cancel
        </Button>
        {!loading && preview && step === 'preview' && !showAddCardForm && (() => {
          const showConfirmBtn = !(hasExisting && amountDue > 0 && displayBillingCycle === 'annual');
          const hasCard = !!defaultCard?.id;
          const showPayAtStripe = (!hasExisting || (hasExisting && amountDue > 0))
            && !(showConfirmBtn && hasCard);
          return (
            <>
              {showPayAtStripe && (
                <Button
                  variant={showConfirmBtn ? 'outlined' : 'contained'}
                  onClick={handlePayAtStripe}
                  disabled={step === 'processing'}
                  sx={{ textTransform: 'none' }}
                >
                  Pay at Stripe
                </Button>
              )}
              {showConfirmBtn && (
                <Button
                  variant="contained"
                  onClick={
                    hasExisting
                      ? handleConfirmPayment
                      : () => hasCard && handleSubscribe(defaultCard.id)
                  }
                  disabled={step === 'processing' || (!hasExisting && !hasCard)}
                >
                  {hasExisting
                    ? isDowngrade && amountDue <= 0
                      ? 'Confirm Downgrade'
                      : amountDue > 0
                        ? 'Confirm & Pay'
                        : 'Confirm'
                    : 'Subscribe'}
                </Button>
              )}
            </>
          );
        })()}
      </DialogActions>
    </Dialog>
  );
};

export default StripePaymentModal;
